import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, fetchConfig, formatApiError } from "@/lib/api";
import { POSITIONS } from "@/lib/jobs";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { PracticeRound } from "@/components/practice/PracticeRound";
import { Leaderboard } from "@/components/practice/Leaderboard";
import { drillSituation } from "@/lib/practice";

const NameForm = ({ code, onJoined }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onJoined(await api.practiceJoin(code, name)); } catch (err) { setError(formatApiError(err)); setBusy(false); }
  };
  return (
    <form className="card max-w-md mx-auto space-y-4 !p-6" onSubmit={submit} data-testid="practice-name-form">
      <h2 className="font-heading text-3xl font-extrabold uppercase">What's your first name?</h2>
      <p className="text-sm font-medium text-[var(--muted)]">So your coach can see how practice is going. No account needed.</p>
      <input data-testid="practice-name-input" className="field-input w-full" required maxLength={30} value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" />
      {error && <p className="text-sm font-bold text-red-600" data-testid="practice-name-error">{error}</p>}
      <button className="btn-primary w-full" data-testid="practice-name-submit" disabled={busy}>{busy && <Loader2 size={18} className="animate-spin" />} Start practice</button>
    </form>
  );
};

const PositionPicker = ({ onPick }) => (
  <div className="card max-w-md mx-auto space-y-4 !p-6" data-testid="practice-position-picker">
    <h2 className="font-heading text-3xl font-extrabold uppercase">Pick your position</h2>
    <div className="grid grid-cols-3 gap-3">
      {POSITIONS.map((p) => (
        <button key={p} className="pos-btn" data-testid={`practice-pos-${p.toLowerCase()}`} onClick={() => onPick(p)}>{p}</button>
      ))}
    </div>
  </div>
);

export default function PracticePage() {
  const { code } = useParams();
  const storeKey = `dp-practice-${code?.toUpperCase()}`;
  const [config, setConfig] = useState(null);
  const [team, setTeam] = useState(null);
  const [error, setError] = useState("");
  const [player, setPlayer] = useState(() => JSON.parse(localStorage.getItem(storeKey) || "null"));
  const [position, setPosition] = useState(null);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [board, setBoard] = useState(null);
  const [drills, setDrills] = useState([]);

  useEffect(() => { fetchConfig().then(setConfig); }, []);
  useEffect(() => { api.join(code).then(setTeam).catch((e) => setError(formatApiError(e))); }, [code]);
  const loadBoard = useCallback(() => api.leaderboard(code).then(setBoard).catch(() => {}), [code]);
  useEffect(() => { loadBoard(); api.joinAssignments(code).then(setDrills).catch(() => {}); }, [code, loadBoard]);
  const myDrills = useMemo(() => drills.filter((d) => !d.position || d.position === position), [drills, position]);
  const preset = useMemo(() => {
    if (!myDrills.length) return null;
    if (round < myDrills.length) return drillSituation(myDrills[round]);
    return round % 3 === 0 ? drillSituation(myDrills[Math.floor(Math.random() * myDrills.length)]) : null;
  }, [myDrills, round]);
  const joined = (p) => { localStorage.setItem(storeKey, JSON.stringify(p)); setPlayer(p); };
  const onResult = useCallback((ok) => setScore((s) => ({ correct: s.correct + (ok ? 1 : 0), total: s.total + 1 })), []);

  if (error) return <PageShell><AppHeader /><div className="card" data-testid="practice-error">{error}</div></PageShell>;
  if (!config || !team) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading practice...</div>;
  const cfg = config.sports[team.sport][team.level];

  return (
    <PageShell>
      <AppHeader>
        <Link to={`/t/${code}`} className="nav-btn" data-testid="practice-back-link"><ArrowLeft size={18} /> Playbook</Link>
      </AppHeader>
      <div className="team-banner" data-testid="practice-banner">
        <div className="min-w-0">
          <p className="ctl-label">Practice · {team.name}</p>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold uppercase tracking-tight">{player ? `Let's go, ${player.name}` : "Practice mode"}</h1>
        </div>
        {player && (
          <div className="flex items-center gap-2">
            <span className="score-pill" data-testid="practice-score">{score.correct}/{score.total}</span>
            {position && <button className="btn-ghost !px-3" data-testid="practice-change-position" onClick={() => { setPosition(null); setRound(0); }}>{position} · Change</button>}
          </div>
        )}
      </div>
      {!player && <NameForm code={code} onJoined={joined} />}
      {player && !position && <PositionPicker onPick={setPosition} />}
      {player && position && (
        <PracticeRound key={`${position}-${round}`} cfg={cfg} code={code} player={player} position={position} preset={preset}
          drillsLeft={Math.max(0, myDrills.length - round)} onResult={onResult} onSaved={loadBoard} onNext={() => setRound((r) => r + 1)} />
      )}
      {player && <div className="max-w-xl"><Leaderboard data={board} meId={player.player_id} /></div>}
    </PageShell>
  );
}
